import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import DiscoverGraph from './DiscoverGraph';
import { usePremium } from '../hooks/usePremium';
import { useLanguage } from '../i18n/LanguageContext';
import './Discover.css';

// Full-view teaser shown to free users instead of the real graph.
const DiscoverTeaser: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  return (
    <div className="discover-teaser">
      <div className="discover-teaser-card">
        <svg viewBox="0 0 240 130" className="discover-teaser-graph" aria-hidden="true">
          <line x1="120" y1="65" x2="55" y2="35" stroke="#5b94f1" strokeWidth="2" opacity="0.6" />
          <line x1="120" y1="65" x2="60" y2="100" stroke="#ea6e6e" strokeWidth="2" opacity="0.6" />
          <line x1="120" y1="65" x2="185" y2="40" stroke="#5becbb" strokeWidth="2" opacity="0.6" />
          <line x1="120" y1="65" x2="180" y2="100" stroke="#efbc65" strokeWidth="2" opacity="0.6" />
          <line x1="185" y1="40" x2="180" y2="100" stroke="#b497f6" strokeWidth="1.5" opacity="0.4" />
          <circle cx="120" cy="65" r="16" fill="#5b94f1" />
          <circle cx="55" cy="35" r="10" fill="#ea6e6e" />
          <circle cx="60" cy="100" r="10" fill="#5becbb" />
          <circle cx="185" cy="40" r="10" fill="#efbc65" />
          <circle cx="180" cy="100" r="10" fill="#b497f6" />
        </svg>
        <h2>{t.discover.teaserTitle}</h2>
        <p>{t.discover.teaserBody}</p>
        <span className="discover-teaser-lock">{t.discover.teaserLock}</span>
        <button className="discover-teaser-btn" onClick={() => navigate('/pricing')}>
          {t.discover.teaserCta}
        </button>
      </div>
    </div>
  );
};

interface Tag {
  id: number;
  name: string;
  color: string;
  text_color: string;
}

interface SourceEntry {
  id: number;
  content: string;
  content_preview: string;
  created_at: string;
  tag_details: Tag[];
  media: any;
  links: any[];
}

interface DiscoverProps {
  // When set, Discover opens straight onto this note's graph — used by the
  // "See the threads" thread icon on each card, so a tap on a note jumps
  // right into Discovery centered on it.
  initialEntryId?: number | null;
  onInitialEntryConsumed?: () => void;
}

const Discover: React.FC<DiscoverProps> = ({ initialEntryId, onInitialEntryConsumed }) => {
  const { t } = useLanguage();
  const { premium, loading: premiumLoading } = usePremium();
  const [entries, setEntries] = useState<SourceEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<SourceEntry | null>(null);
  const [isLoadingEntries, setIsLoadingEntries] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  const fetchEntries = useCallback(async (pageNum: number, query: string, append: boolean) => {
    try {
      let data: SourceEntry[];
      let more = false;

      if (query.trim()) {
        const response = await api.get(`/entries/search/?q=${encodeURIComponent(query)}`);
        data = response.data;
      } else {
        const response = await api.get(`/entries/latest/?page=${pageNum}&page_size=40`);
        data = response.data.results;
        more = response.data.has_more;
      }

      // Only show entries that have tags and actual text content (not image-only posts)
      const tagged = data.filter((e: any) => {
        if (!e.tag_details || e.tag_details.length === 0) return false;
        const text = (e.content || '').replace(/\[\[image\]\]/g, '').replace(/<[^>]*>/g, '').trim();
        return text.length > 0;
      });
      setEntries(prev => append ? [...prev, ...tagged] : tagged);
      setHasMore(more);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setIsLoadingEntries(false);
    }
  }, []);

  useEffect(() => {
    setIsLoadingEntries(true);
    setPage(1);
    const timeout = setTimeout(() => {
      fetchEntries(1, searchQuery, false);
    }, searchQuery ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [searchQuery, fetchEntries]);

  const loadMore = () => {
    if (!hasMore || searchQuery) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchEntries(nextPage, '', true);
  };

  const handleSelectEntry = (entry: SourceEntry) => {
    // The graph fetches and branches the related entries itself.
    setSelectedEntry(entry);
  };

  // Arriving from a card's "See the threads" icon: fetch that note and drop
  // straight into its graph, no manual pick needed. Premium-gated like the
  // rest of Discovery.
  useEffect(() => {
    if (!initialEntryId || premiumLoading || !premium) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/entries/${initialEntryId}/`);
        if (!cancelled) setSelectedEntry(response.data);
      } catch (err) {
        console.error('Failed to open note in Discover:', err);
      } finally {
        if (!cancelled) onInitialEntryConsumed?.();
      }
    })();
    return () => { cancelled = true; };
  }, [initialEntryId, premium, premiumLoading, onInitialEntryConsumed]);

  const handleListScroll = () => {
    if (!listRef.current || !hasMore || searchQuery) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMore();
    }
  };

  const stripHtml = (html: string) => {
    const clean = html.replace(/\[\[image\]\]/g, '').replace(/<[^>]*>/g, '');
    return clean.substring(0, 280) || t.discover.noText;
  };

  // Free users get a teaser of the graph instead of the real thing
  if (!premiumLoading && !premium) {
    return (
      <div className="discover-container">
        <DiscoverTeaser />
      </div>
    );
  }

  return (
    <div className="discover-container">
      <div className="discover-sidebar">
        <div className="discover-sidebar-header">
          <h2>{t.discover.title}</h2>
          <p className="discover-hint">{t.discover.hint}</p>
          <input
            type="text"
            className="discover-search"
            placeholder={t.home.searchNotesPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="discover-entry-list" ref={listRef} onScroll={handleListScroll}>
          {isLoadingEntries ? (
            <div className="discover-loading">{t.common.loading}</div>
          ) : entries.length === 0 ? (
            <div className="discover-empty">{t.discover.noTaggedNotes}</div>
          ) : (
            entries.map(entry => (
              <button
                key={entry.id}
                className={`discover-entry-item ${selectedEntry?.id === entry.id ? 'selected' : ''}`}
                onClick={() => handleSelectEntry(entry)}
              >
                <div className="discover-entry-preview">
                  {stripHtml(entry.content)}
                </div>
                <div className="discover-entry-tags">
                  {entry.tag_details.slice(0, 4).map(tag => (
                    <span
                      key={tag.id}
                      className="discover-tag"
                      style={{ backgroundColor: tag.color, color: tag.text_color }}
                    >
                      {tag.name}
                    </span>
                  ))}
                  {entry.tag_details.length > 4 && (
                    <span className="discover-tag-more">+{entry.tag_details.length - 4}</span>
                  )}
                </div>
              </button>
            ))
          )}
          {hasMore && !searchQuery && !isLoadingEntries && (
            <button className="discover-load-more" onClick={loadMore}>{t.discover.loadMore}</button>
          )}
        </div>
      </div>

      <div className="discover-main">
        {!selectedEntry ? (
          <div className="discover-placeholder">
            <div className="discover-placeholder-arrow">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </div>
            <div className="discover-placeholder-icon">
              <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <circle cx="6" cy="6" r="2"></circle>
                <circle cx="18" cy="8" r="2"></circle>
                <circle cx="8" cy="18" r="2"></circle>
                <circle cx="18" cy="18" r="2"></circle>
                <path d="M8 6h8M7 8l1 8M10 18h6M18 10v6" strokeDasharray="2 2"></path>
              </svg>
            </div>
            <p className="discover-placeholder-title">{t.discover.placeholderTitle}</p>
            <p>{t.discover.placeholderBody}</p>
            <p className="discover-placeholder-sub">{t.discover.placeholderSub}</p>
          </div>
        ) : (
          <DiscoverGraph root={selectedEntry} />
        )}
      </div>
    </div>
  );
};

export default Discover;
